import { useEffect, useRef, useState } from "react";
import { StickyNote, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// =====================================================================
// ToothNoteDialog — single canonical modal used by both Tx Plan and
// Charting for the per-tooth note pill. Built on the shadcn / Radix
// Dialog so it gets focus-trap, Esc-to-close, click-overlay-to-close,
// and accessible labelling for free.
//
// Parent owns persistence:
//   - onSave(text)  — return a Promise. If it rejects, the modal stays
//                     open and surfaces the error. If it resolves, the
//                     modal closes.
//   - onDelete()    — clears the note. Same async contract as onSave.
//
// readOnly view (snapshot history): no editing UI, just the text + a
// Close button.
// =====================================================================

const MAX_LEN = 1000;

export default function ToothNoteDialog({
  fdi,
  initialText = "",
  readOnly = false,
  busy: busyProp = false,
  onSave,
  onDelete,
  onClose,
  // Optional context line — pass procedure summaries you want shown
  // under the title, e.g. "Composite Restoration (M, O), Crown".
  contextLine = null,
  // Optional chart-finding note text. When present, rendered as a
  // read-only block above the editable textarea so the dentist sees
  // their own clinical observation while writing the plan-specific
  // tooth note. Independent of the editable plan note — never copies
  // into the textarea, never gets saved by onSave.
  chartNote = null,
}) {
  const [text, setText] = useState(initialText);
  const [internalBusy, setInternalBusy] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);
  const busy = busyProp || internalBusy;

  // Reset draft + scroll caret to end whenever the dialog is opened
  // for a (possibly different) tooth. Tracking BOTH fdi and
  // initialText guards against stale text when the user switches teeth
  // without first closing the modal.
  useEffect(() => {
    setText(initialText || "");
    setError(null);
    if (readOnly) return;
    const t = setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const n = el.value.length;
      try { el.setSelectionRange(n, n); } catch { /* noop */ }
    }, 30);
    return () => clearTimeout(t);
  }, [fdi, initialText, readOnly]);

  const open = fdi != null;
  const trimmed = text.trim();
  const initialTrimmed = (initialText || "").trim();
  const hasExisting = initialTrimmed.length > 0;
  const dirty = trimmed !== initialTrimmed;
  const canSave = !readOnly && !busy && trimmed.length > 0 && dirty;

  const runWithGuard = async (fn) => {
    if (!fn) return;
    setError(null);
    setInternalBusy(true);
    try {
      const result = fn();
      if (result && typeof result.then === "function") {
        await result;
      }
      onClose?.();
    } catch (e) {
      setError(e?.message || "Could not save note. Please try again.");
    } finally {
      setInternalBusy(false);
    }
  };

  const handleSave = () => runWithGuard(() => onSave?.(trimmed));
  const handleDelete = () => runWithGuard(() => onDelete?.());

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid={`tooth-note-dialog-${fdi ?? ""}`}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSave) {
            e.preventDefault();
            handleSave();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <StickyNote className="w-4 h-4 text-amber-600" />
            </span>
            <span>
              Note for tooth #{fdi ?? ""}
              <span className="ml-2 text-xs font-normal text-amber-600">
                {readOnly ? "(read-only)" : (hasExisting ? "Edit note" : "Add note")}
              </span>
            </span>
          </DialogTitle>
          <DialogDescription>
            Free-text note attached to this tooth. Shows under the tooth in the plan list, on the chart pill, and on the patient PDF.
          </DialogDescription>
        </DialogHeader>

        {contextLine ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
            <span className="font-medium text-slate-600">On this tooth:</span> {contextLine}
          </div>
        ) : null}

        {chartNote ? (
          <div
            className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
            data-testid={`tooth-note-chart-context-${fdi ?? ""}`}
          >
            <p className="font-semibold text-amber-900 mb-0.5">Chart note (read-only)</p>
            <p className="whitespace-pre-wrap text-amber-900/90">{chartNote}</p>
          </div>
        ) : null}

        {readOnly ? (
          <p
            className="text-sm text-slate-700 whitespace-pre-wrap min-h-[6rem] py-2"
            data-testid="tooth-note-readonly-text"
          >
            {initialText || <span className="italic text-slate-400">No note recorded.</span>}
          </p>
        ) : (
          <>
            <label htmlFor={`tooth-note-input-${fdi}`} className="sr-only">
              Note text for tooth {fdi}
            </label>
            <textarea
              ref={textareaRef}
              id={`tooth-note-input-${fdi}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={MAX_LEN}
              rows={6}
              placeholder="e.g. Patient prefers porcelain — discuss timeline."
              data-testid="tooth-note-input"
              disabled={busy}
              className="w-full px-3 py-2 text-sm text-slate-700 rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 resize-y disabled:opacity-60"
            />
            <div className="text-[11px] text-slate-400 -mt-2">
              {text.length}/{MAX_LEN} · ⌘+Enter to save · Esc to cancel
            </div>
          </>
        )}

        {error ? (
          <div
            role="alert"
            className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2.5 py-1.5"
          >
            {error}
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          {!readOnly && hasExisting && onDelete ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              data-testid="tooth-note-delete"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-red-200 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-40"
              aria-label="Delete note"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          ) : null}
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="tooth-note-cancel"
            className="px-4 py-2 text-sm border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              data-testid="tooth-note-save"
              className="px-5 py-2 text-sm font-semibold bg-amber-500 text-white rounded-md hover:bg-amber-600 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <StickyNote className="w-3.5 h-3.5" />}
              {busy ? "Saving…" : "Save note"}
            </button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
